import { useEffect } from 'react';

const BASE_URL = 'https://becomeresumeready.com';

/**
 * useSEO — sets per-page title, canonical, description, og tags, and JSON-LD.
 * Call once per page-level component with a static config object.
 *
 * @param {object} opts
 * @param {string}  opts.title        — <title> + og:title + twitter:title
 * @param {string}  opts.description  — meta description + og:description + twitter:description
 * @param {string}  [opts.canonical]  — canonical URL (defaults to current path)
 * @param {string}  [opts.keywords]   — comma-separated keywords (updates meta[name=keywords])
 * @param {object|object[]} [opts.jsonLd] — one or more JSON-LD objects to inject as a <script> tag
 */
export const useSEO = ({ title, description, canonical, keywords, jsonLd }) => {
  useEffect(() => {
    if (title) {
      document.title = title;
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', title);
      const twTitle = document.querySelector('meta[name="twitter:title"]');
      if (twTitle) twTitle.setAttribute('content', title);
    }

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonical || `${BASE_URL}${window.location.pathname}`;

    // OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonical || `${BASE_URL}${window.location.pathname}`);

    if (description) {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', description);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', description);
      const twDesc = document.querySelector('meta[name="twitter:description"]');
      if (twDesc) twDesc.setAttribute('content', description);
    }

    if (keywords) {
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute('content', keywords);
    }

    // JSON-LD — inject / replace a page-specific <script> block
    if (jsonLd) {
      const existing = document.querySelector('script[data-seo="page-jsonld"]');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'page-jsonld');
      script.textContent = JSON.stringify(Array.isArray(jsonLd) ? { '@context': 'https://schema.org', '@graph': jsonLd } : jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup: remove page-specific JSON-LD when component unmounts
    return () => {
      const el = document.querySelector('script[data-seo="page-jsonld"]');
      if (el) el.remove();
    };
  }, [title, description, canonical, keywords, jsonLd]); // eslint-disable-line react-hooks/exhaustive-deps
};
